import { useEffect, useRef } from 'react';
import { AttachFileButton } from './AttachFileButton';
import { composerChrome } from './composerChrome.css';
import { AttachmentChips } from './AttachmentChips';
import {
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  StatusTag,
  theme,
  type Message,
} from '@agentisai/design-system';
import { type TurnEvent, type WorkstreamMode } from '@agentisai/shared-types';

/**
 * The platform agents a user may invite into a workstream conversation
 * (mirrors `apps/workstreamagent/api/agents/platform/*` minus `discovery`,
 * which is the pre-creation interview agent only — see `create-chat.ts`).
 * Hand-maintained until the registry is exposed to the BFF (follow-on).
 */
export const INVITABLE_AGENTS: readonly { name: string; label: string }[] = [
  { name: 'architect', label: 'Architect' },
  { name: 'delivery', label: 'Delivery' },
  { name: 'engineer', label: 'Engineer' },
  { name: 'governance', label: 'Governance' },
  { name: 'qa', label: 'QA' },
];

/**
 * Display label for an agent name. Falls back to capitalising the raw name, so
 * an agent outside the invitable roster (`discovery`, `librarian`) still reads
 * as a name rather than a slug. Exported because the transcript renderer labels
 * per-message speakers with it too (ADR-0054).
 */
export const agentLabel = (name: string): string =>
  INVITABLE_AGENTS.find((a) => a.name === name)?.label ??
  name.charAt(0).toUpperCase() + name.slice(1);

/**
 * Front-of-house roles: they interview and route rather than build. Kept on the
 * purple `mira` treatment — the tone the create-flow interview already uses.
 */
const COORDINATION_AGENTS = new Set(['discovery', 'librarian']);

/**
 * Bubble variant for an assistant turn, which is what colours the avatar ring.
 * Every reply used to render `mira`, so six agents were visually identical and
 * the name was the only clue — and two of them ('Discovery' / 'Delivery') even
 * share an avatar initial. Splitting coordination (purple) from specialists
 * (blue) makes "who answered this" legible before you read anything.
 */
export function agentVariant(agents: readonly string[] | undefined): 'mira' | 'agent' {
  const first = agents?.[0];
  return first !== undefined && !COORDINATION_AGENTS.has(first) ? 'agent' : 'mira';
}

export interface WorkspaceConversationProps {
  title: string;
  /**
   * Passed through to `ChatPanel`. Default true (full-pane conversation);
   * false when rendered as the canonical 40% sibling column beside the
   * centre-pane file viewer (the DS prototype shell layout).
   */
  fluid?: boolean;
  messages: Message[];
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  maxLength?: number;
  pending?: boolean;
  sendError?: string | null;
  /** Currently selected invite-agent hint, or `null` for router-auto. */
  invitedAgent: string | null;
  onInviteAgent: (agentName: string | null) => void;
  /**
   * Librarian routing mode, as last reported by the server. `discovery`
   * locks the conversation to the discovery interview — the invite row is
   * replaced by the exit affordance until the user ends the interview.
   */
  mode?: WorkstreamMode;
  /**
   * Agents that produced the latest reply, in execution order (server-reported
   * `data.agents`). Staged turns read as a pipeline (e.g. Engineer → QA).
   */
  activeAgents?: readonly string[];
  /** Ends the discovery interview (sends the `exitDiscovery` action). */
  onExitDiscovery?: () => void;
  /**
   * Live progress checkpoints for the in-flight turn (ADR-0042) — the latest
   * renders as a status line while `pending`; the full trail sits behind a
   * disclosure. Cleared by the parent when the turn resolves.
   */
  turnEvents?: readonly TurnEvent[];
  /**
   * Consensus-tracker conflict detail for the latest turn, or `null` when the
   * agents agreed. Rendered as a warning banner (Librarian flow step 8b —
   * "surface conflict to user").
   */
  conflict?: string | null;
  /**
   * Files staged for the next turn (parent-owned, like the composer draft).
   * Staged, not uploaded — the parent flushes them on send.
   */
  stagedFiles: readonly File[];
  /** Files were picked. Validation + staging are the parent's job. */
  onFilesPicked: (files: File[]) => void;
  /** Drop one staged file before sending. */
  onRemoveFile: (file: File) => void;
  /** Name of the file currently uploading during a flush, or `null`. */
  uploadingFilename?: string | null;
  /** Upload progress 0–100, meaningful only while {@link uploadingFilename} is set. */
  uploadProgress?: number;
  /** Attach/upload failure copy, already user-facing. */
  attachError?: string | null;
}

/**
 * WorkspaceConversation — the live conversation for an already-created
 * workstream (post-creation). Composes the same DS primitives as
 * `CreateWorkstreamPanel` (ChatPanel + MessageBubble + Composer), rendered
 * `fluid` inline in the WorkspaceShell main pane rather than in a Modal.
 * State is fully owned by the parent (HomePage) — this component renders only.
 */
export function WorkspaceConversation({
  title,
  fluid = true,
  messages,
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  sendError,
  invitedAgent,
  onInviteAgent,
  mode = 'normal',
  activeAgents = [],
  turnEvents = [],
  onExitDiscovery,
  conflict,
  stagedFiles,
  onFilesPicked,
  onRemoveFile,
  uploadingFilename = null,
  uploadProgress = 0,
  attachError = null,
}: WorkspaceConversationProps) {
  // Auto-scroll to the latest turn — same recipe as CreateWorkstreamPanel
  // (imperative DOM sync, the sanctioned effect+ref use, frontend-architecture R24/R25).
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  // Cursor-style mode indicator: which routing mode the Librarian is in, and
  // which agents produced the latest reply (in execution order — a staged
  // build→review turn reads Engineer → QA).
  const statusRow = (
    // role=group (not status): a live region here would announce every turn,
    // and the conflict banner below is the page's one role=status element.
    <div
      role="group"
      aria-label="Conversation status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.space['1_5'],
        paddingInline: theme.space[4],
      }}
    >
      {mode === 'discovery' ? (
        <StatusTag tone="info">Discovery mode</StatusTag>
      ) : invitedAgent ? (
        <StatusTag tone="info">Invited: {agentLabel(invitedAgent)}</StatusTag>
      ) : (
        <StatusTag tone="neutral">Auto routing</StatusTag>
      )}
      {activeAgents.length > 0 && (
        <StatusTag tone="success">Last reply: {activeAgents.map(agentLabel).join(' → ')}</StatusTag>
      )}
    </div>
  );

  const composerLeadingActions = (
    <>
      <AttachFileButton onFilesPicked={onFilesPicked} disabled={uploadingFilename !== null} />
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <ChatPanel
      fluid={fluid}
      caption={
        mode === 'discovery'
          ? 'Discovery interview in progress — the discovery agent leads until you exit'
          : 'Ask a question, or invite a specialist agent below'
      }
      title={title}
      awaitingReply={pending}
      // Name who is about to answer. The DS default is "Workstream Agent is
      // typing", which showed on EVERY turn — including the ones a named
      // specialist handled. Left undefined under auto-routing, where the
      // Librarian genuinely has not picked an agent yet: the generic default is
      // the honest label there, not a guess.
      typingLabel={
        mode === 'discovery'
          ? 'Discovery is typing'
          : invitedAgent
            ? `${agentLabel(invitedAgent)} is typing`
            : undefined
      }
      messages={
        <>
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          <div ref={bottomRef} aria-hidden="true" />
        </>
      }
      composer={
        <>
          <div className={composerChrome}>
            {statusRow}
            {pending && turnEvents.length > 0 && (
              // Live turn narration (ADR-0042): the latest checkpoint under the
              // typing indicator, the full trail behind a native disclosure.
              // polite live region — checkpoint granularity (a handful per
              // turn), not a token stream, so announcements stay reasonable.
              <div style={{ paddingInline: theme.space[4] }}>
                <details>
                  <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                    <span role="status" aria-live="polite">
                      <StatusTag tone="info">{turnEvents[turnEvents.length - 1]!.label}</StatusTag>
                    </span>
                  </summary>
                  <ol
                    aria-label="Turn activity"
                    style={{
                      margin: `${theme.space['1_5']} 0 0`,
                      paddingLeft: theme.space[5],
                      fontSize: theme.typography.fontSize.caption,
                      color: theme.color.neutral.muted,
                    }}
                  >
                    {turnEvents.map((e, i) => (
                      <li key={`${e.at}-${i}`}>{e.label}</li>
                    ))}
                  </ol>
                </details>
              </div>
            )}
            {mode === 'discovery' ? (
              // Discovery locks routing server-side — inviting is inert, so the
              // row is replaced by the one meaningful action: exit the interview.
              // If this chrome grows beyond a single button (banner, distinct
              // composer state), promote it to a DS ChatPanel variant + story (R0).
              <div
                style={{
                  display: 'flex',
                  gap: theme.space['1_5'],
                  paddingInline: theme.space[4],
                }}
              >
                <Button size="sm" variant="secondary" disabled={pending} onClick={onExitDiscovery}>
                  Exit Discovery Interview
                </Button>
              </div>
            ) : (
              <div
                role="group"
                aria-label="Invite an agent"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.space['1_5'],
                  paddingInline: theme.space[4],
                }}
              >
                <Button
                  size="sm"
                  variant={invitedAgent === null ? 'secondary' : 'ghost'}
                  aria-pressed={invitedAgent === null}
                  onClick={() => onInviteAgent(null)}
                >
                  Auto
                </Button>
                {INVITABLE_AGENTS.map((agent) => (
                  <Button
                    key={agent.name}
                    size="sm"
                    variant={invitedAgent === agent.name ? 'secondary' : 'ghost'}
                    aria-pressed={invitedAgent === agent.name}
                    onClick={() => onInviteAgent(agent.name)}
                  >
                    {agent.label}
                  </Button>
                ))}
              </div>
            )}
            <AttachmentChips
              files={stagedFiles}
              onRemove={onRemoveFile}
              uploadingName={uploadingFilename}
              uploadProgress={uploadProgress}
            />
            {attachError && (
              <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="danger">{attachError}</Callout>
              </div>
            )}
            {conflict && (
              // Advisory, not an error: polite live region so SRs announce the
              // disagreement without interrupting (Callout carries no role itself).
              <div role="status" aria-live="polite" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="warn" title="Agents disagreed on this reply">
                  {conflict}
                </Callout>
              </div>
            )}
            {sendError && (
              <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                <Callout tone="danger">{sendError}</Callout>
              </div>
            )}
          </div>
          <Composer
            value={value}
            onChange={onChange}
            onSend={onSend}
            sendDisabled={sendDisabled}
            placeholder={
              mode === 'discovery'
                ? 'Reply to the discovery agent…'
                : invitedAgent
                  ? `Ask ${INVITABLE_AGENTS.find((a) => a.name === invitedAgent)?.label}…`
                  : 'Ask a question…'
            }
            leadingActions={composerLeadingActions}
          />
          {maxLength != null && (value?.length ?? 0) > maxLength && (
            // Below the Composer, so outside `composerChrome` — it owns its own
            // top gap rather than inheriting the chrome column's rhythm.
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">
                Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                {maxLength.toLocaleString()} characters. Shorten it to send.
              </Callout>
            </div>
          )}
        </>
      }
    />
  );
}
WorkspaceConversation.displayName = 'WorkspaceConversation';
